import type { Layout } from 'react-grid-layout';

export const METRICS = [
  'venues',
  'brands',
  'users',
  'mrr',
  'addons',
  'publishes',
  'failures',
] as const;
export const GROUPINGS = ['tenant', 'addon', 'day'] as const;
export const RANGES = ['7d', '30d', '90d'] as const;
export const CHARTS = ['hbar', 'bar', 'line', 'area'] as const;

export type Metric = (typeof METRICS)[number];
export type Grouping = (typeof GROUPINGS)[number];
export type Range = (typeof RANGES)[number];
export type ChartType = (typeof CHARTS)[number];

export const METRIC_LABELS: Record<Metric, string> = {
  venues: 'Venues',
  brands: 'Brands',
  users: 'Users',
  mrr: 'Monthly recurring',
  addons: 'Add-ons installed',
  publishes: 'Publishes',
  failures: 'Failed events',
};

export const GROUPING_LABELS: Record<Grouping, string> = {
  tenant: 'by tenant',
  addon: 'by add-on',
  day: 'per day',
};
export const RANGE_LABELS: Record<Range, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
};
export const CHART_LABELS: Record<ChartType, string> = {
  hbar: 'Horizontal bars',
  bar: 'Columns',
  line: 'Line',
  area: 'Area',
};

/** Which groupings and ranges each metric supports. Mirrors /metrics/catalogue on the API. */
export const allowedGroupings = (metric: Metric): Grouping[] =>
  metric === 'publishes' || metric === 'failures' ? ['day', 'tenant'] : ['tenant', 'addon'];

export const supportsRange = (metric: Metric): boolean =>
  metric === 'publishes' || metric === 'failures';

export const allowedCharts = (grouping: Grouping): ChartType[] =>
  grouping === 'day' ? ['line', 'area', 'bar'] : ['hbar', 'bar'];

export type WidgetKind = 'metric' | 'kpis' | 'health' | 'aws';

export interface WidgetSpec {
  id: string;
  kind: WidgetKind;
  metric: Metric;
  group_by: Grouping;
  range: Range;
  chart: ChartType;
  limit: number | null;
}

export interface DashboardState {
  version: 1;
  layout: Layout[];
  widgets: WidgetSpec[];
}

export const metricWidget = (
  id: string,
  metric: Metric,
  group_by: Grouping,
  chart: ChartType,
  range: Range = '30d',
): WidgetSpec => ({
  id,
  kind: 'metric',
  metric,
  group_by,
  range,
  chart,
  limit: null,
});

export const DEFAULT_STATE: DashboardState = {
  version: 1,
  widgets: [
    {
      id: 'kpis',
      kind: 'kpis',
      metric: 'venues',
      group_by: 'tenant',
      range: '30d',
      chart: 'hbar',
      limit: null,
    },
    {
      id: 'health',
      kind: 'health',
      metric: 'failures',
      group_by: 'tenant',
      range: '30d',
      chart: 'hbar',
      limit: null,
    },
    metricWidget('mrr', 'mrr', 'tenant', 'hbar'),
    metricWidget('venues', 'venues', 'tenant', 'hbar'),
    metricWidget('publishes', 'publishes', 'day', 'line', '30d'),
    metricWidget('addons', 'addons', 'addon', 'hbar'),
    {
      id: 'aws',
      kind: 'aws',
      metric: 'venues',
      group_by: 'tenant',
      range: '90d',
      chart: 'bar',
      limit: null,
    },
  ],
  layout: [
    { i: 'kpis', x: 0, y: 0, w: 12, h: 3, minW: 6, minH: 3 },
    { i: 'health', x: 0, y: 3, w: 4, h: 6, minW: 3, minH: 5 },
    { i: 'mrr', x: 4, y: 3, w: 4, h: 6, minW: 3, minH: 4 },
    { i: 'venues', x: 8, y: 3, w: 4, h: 6, minW: 3, minH: 4 },
    { i: 'publishes', x: 0, y: 9, w: 8, h: 6, minW: 4, minH: 4 },
    { i: 'addons', x: 8, y: 9, w: 4, h: 6, minW: 3, minH: 4 },
    { i: 'aws', x: 0, y: 15, w: 8, h: 6, minW: 4, minH: 4 },
  ],
};

export const titleFor = (spec: WidgetSpec): string => {
  if (spec.kind === 'kpis') {
    return 'Headline numbers';
  }
  if (spec.kind === 'health') {
    return 'Tenant health';
  }
  if (spec.kind === 'aws') {
    return 'AWS cost by account';
  }
  const base = `${METRIC_LABELS[spec.metric]} ${GROUPING_LABELS[spec.group_by]}`;
  return supportsRange(spec.metric) ? `${base}, ${RANGE_LABELS[spec.range].toLowerCase()}` : base;
};

/** Coerce a spec after the user changes one field so the combination stays valid. */
export const normaliseSpec = (spec: WidgetSpec): WidgetSpec => {
  const groupings = allowedGroupings(spec.metric);
  const group_by = groupings.includes(spec.group_by) ? spec.group_by : groupings[0];
  const charts = allowedCharts(group_by);
  const chart = charts.includes(spec.chart) ? spec.chart : charts[0];
  return { ...spec, group_by, chart };
};

export const isDashboardState = (value: unknown): value is DashboardState => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Partial<DashboardState>;
  return (
    candidate.version === 1 &&
    Array.isArray(candidate.layout) &&
    Array.isArray(candidate.widgets) &&
    candidate.widgets.every(
      (widget) => typeof widget?.id === 'string' && typeof widget?.kind === 'string',
    )
  );
};
